// src/components/ui/Button.jsx
import { forwardRef } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/utils/cn'

const Button = forwardRef(({
                               children,
                               className,
                               variant = 'primary',
                               size = 'md',
                               icon: Icon,
                               iconPosition = 'left',
                               iconProps = {},
                               disabled = false,
                               loading = false,
                               onClick,
                               ...props
                           }, ref) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

    const variants = {
        primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500',
        secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900 focus:ring-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white',
        danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500',
        success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500',
        warning: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500',
        outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500 dark:hover:bg-primary-900/20',
        ghost: 'hover:bg-gray-100 text-gray-600 focus:ring-gray-500 dark:hover:bg-gray-800 dark:text-gray-300'
    }

    const sizes = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-lg'
    }

    const iconSizes = {
        sm: 16,
        md: 18,
        lg: 20,
        xl: 24
    }

    return (
        <motion.button
            ref={ref}
            className={cn(
                baseClasses,
                variants[variant],
                sizes[size],
                className
            )}
            disabled={disabled || loading}
            onClick={onClick}
            whileTap={{ scale: disabled ? 1 : 0.98 }}
            whileHover={{ scale: disabled ? 1 : 1.02 }}
            {...props}
        >
            {Icon && iconPosition === 'left' && (
                <Icon
                    size={iconSizes[size]}
                    className={cn('mr-2', iconProps.className)}
                    {...iconProps}
                />
            )}
            {children}
            {Icon && iconPosition === 'right' && (
                <Icon
                    size={iconSizes[size]}
                    className={cn('ml-2', iconProps.className)}
                    {...iconProps}
                />
            )}
        </motion.button>
    )
})

Button.displayName = 'Button'

export default Button
